import { useState } from "react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import type { TimeseriesBucket } from "../types/api";

type MetricKey = "generations" | "tokensIn" | "tokensOut" | "costUsd";

interface MetricOption {
  key: MetricKey;
  label: string;
  color: string;
  kind: "line" | "area";
}

const METRIC_OPTIONS: MetricOption[] = [
  { key: "costUsd", label: "Cost (USD)", color: "#2563eb", kind: "area" },
  { key: "generations", label: "Generations", color: "#16a34a", kind: "line" },
  { key: "tokensIn", label: "Tokens In", color: "#9333ea", kind: "line" },
  { key: "tokensOut", label: "Tokens Out", color: "#ea580c", kind: "line" },
];

interface TimeSeriesChartProps {
  buckets: TimeseriesBucket[];
  days: number;
  onDaysChange: (days: number) => void;
}

// V2M-DEV-012: "1" added so PK can read cost/generations for the last 24h
// (M-A's own "zwraca dane za ostatnią dobę" requirement) without leaving
// this chart — previously the shortest window was 7 days.
const RANGE_OPTIONS = [1, 7, 30, 90];

export function TimeSeriesChart({ buckets, days, onDaysChange }: TimeSeriesChartProps) {
  const [metric, setMetric] = useState<MetricKey>("costUsd");
  const selected = METRIC_OPTIONS.find((m) => m.key === metric) ?? METRIC_OPTIONS[0];

  return (
    <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-6 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex gap-1">
          {METRIC_OPTIONS.map((opt) => (
            <button
              key={opt.key}
              type="button"
              onClick={() => setMetric(opt.key)}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                metric === opt.key
                  ? "bg-slate-900 text-white"
                  : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <div className="flex gap-1">
          {RANGE_OPTIONS.map((r) => (
            <button
              key={r}
              type="button"
              onClick={() => onDaysChange(r)}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                days === r
                  ? "bg-blue-600 text-white"
                  : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              }`}
            >
              {r}d
            </button>
          ))}
        </div>
      </div>

      {buckets.length === 0 ? (
        <div className="text-slate-500 text-sm py-12 text-center">
          No data in the selected window.
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={320}>
          {selected.kind === "area" ? (
            <AreaChart data={buckets}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="date" tick={{ fontSize: 12 }} stroke="#94a3b8" />
              <YAxis tick={{ fontSize: 12 }} stroke="#94a3b8" />
              <Tooltip />
              <Area
                type="monotone"
                dataKey={selected.key}
                stroke={selected.color}
                fill={selected.color}
                fillOpacity={0.15}
              />
            </AreaChart>
          ) : (
            <LineChart data={buckets}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="date" tick={{ fontSize: 12 }} stroke="#94a3b8" />
              <YAxis tick={{ fontSize: 12 }} stroke="#94a3b8" />
              <Tooltip />
              <Line
                type="monotone"
                dataKey={selected.key}
                stroke={selected.color}
                strokeWidth={2}
                dot={false}
              />
            </LineChart>
          )}
        </ResponsiveContainer>
      )}
    </div>
  );
}
